// Settings sheet and language: theme, pin badge, live toggle and language picker, plus the palette
// and legend that follow them. `app` gives live access to page state owned by app.mjs.
import {renderLegendMarkers} from './legend.mjs';
import {palette} from './palette.mjs';
import {S, translateUI} from './strings.mjs';
import {savePreference, resolveTheme, THEME_MODES} from './settings.mjs';
import {syncSitePalette} from './site-layers.mjs';
import {refreshPinSprites} from './pins.mjs';
import {refreshHitSprites} from './hits.mjs';
import {styleURL} from './map-styles.mjs';
import {loadLanguage, number} from './i18n.mjs';
import {languagePicker} from './language-picker.mjs';
import {filterControls} from './filters.mjs';
import {installMenu} from './menu.mjs';
import {installDrawer} from './drawer.mjs';
import {chipControls, chipsCarousel, chipsPlacement, nextPinMode, syncPinChip} from './chips.mjs';
import {installDock} from './dock.mjs';
import {installThemeColor} from './theme-color.mjs';

const $ = id => document.getElementById(id);

export function appearance(app) {
  const {prefs} = app;
  function renderLegend() {
    renderLegendMarkers($('legend'), S.legend, prefs.theme, app.profile);
  }
  function syncPalette() {
    const {map} = app, profile = app.profile = palette(prefs.theme);
    if (app.sourceReady) { syncSitePalette(map,prefs.theme,profile); refreshPinSprites(map,profile); refreshHitSprites(map,profile); map.triggerRepaint(); }
    app.selection?.refresh();
    renderLegend();
  }
  function syncSettings() {
    for(const mode of ['kw','price','off']) $('secondary-'+mode).setAttribute('aria-pressed',String(prefs.secondary===mode));
    syncPinChip(prefs.secondary);
    for(const mode of THEME_MODES) $('theme-'+mode).setAttribute('aria-pressed',String(prefs.mode===mode));
    $('live-toggle').checked=prefs.live; $('live-badge').hidden=!prefs.live;
    if(!prefs.live) app.liveInfo?.open(false);
  }
  return {renderLegend, syncPalette, syncSettings};
}

export function installSettings(app, {switchStyle, media, storage}, {renderLegend, syncPalette, syncSettings}) {
  const {prefs} = app;
  const setTheme = theme => {
    if (prefs.theme === theme) { syncSettings(); return; }
    prefs.theme=theme; syncPalette(); document.documentElement.dataset.theme=theme; renderLegend(); syncSettings();
    switchStyle(styleURL(theme));
  };
  // Auto follows prefers-color-scheme live, also while the page is open.
  media.addEventListener('change',event=>{if(prefs.mode==='auto')setTheme(resolveTheme('auto',event.matches));});
  for(const mode of THEME_MODES) $('theme-'+mode).onclick=()=>{
    prefs.mode=mode;prefs.explicitTheme=mode==='auto'?null:mode;savePreference('theme',mode,storage);setTheme(resolveTheme(mode,media.matches));
  };
  // The pin value (kW, price, off) is one preference: the menu segments and the pin switch both set it.
  const setSecondary=mode=>{prefs.secondary=mode;savePreference('secondary',mode,storage);syncSettings();app.selection?.refresh();if(mode==='price')app.refreshIndex();else app.pipeline.schedule();};
  for(const mode of ['kw','price','off']) $('secondary-'+mode).onclick=()=>setSecondary(mode);
  $('chip-pin').onclick=()=>setSecondary(nextPinMode(prefs.secondary));
  $('live-toggle').onchange=()=>{prefs.live=$('live-toggle').checked;savePreference('live',prefs.live?'on':'off',storage);app.liveControl.setEnabled(prefs.live);syncSettings();};
  let languageRequest=0;
  const applyLanguage=async(lang,save=true)=>{
    const request=++languageRequest;
    prefs.lang=lang;if(save)savePreference('lang',lang,storage);
    await loadLanguage(lang);
    if(request!==languageRequest)return;
    const {locator, detailId} = app;
    translateUI(app.staleDays);picker.render(lang);app.syncFilters();app.filterBadge();renderLegend();
    for(const el of document.querySelectorAll('[data-number]'))el.textContent=number(Number(el.dataset.number));
    $('position-button').setAttribute('aria-label',locator.state==='on'?S.followStop:locator.state==='off'?S.locate:S.locating);
    const positionMarker=document.querySelector('.position');if(positionMarker)positionMarker.setAttribute('aria-label',S.myPosition);
    app.pipeline.schedule();app.selection?.refresh();app.toast.hide();app.liveInfo.setState($('live-badge').dataset.state);app.renderHitsChip();
    if(detailId)app.details(detailId,false);
  };
  const picker=languagePicker({value:prefs.lang,onChange:lang=>applyLanguage(lang)});
  // Auto follows a browser language change while the page is open.
  addEventListener('languagechange',()=>{if(prefs.lang==='auto')applyLanguage('auto',false);else picker.render();});
}

// Filter sheet, filter and menu buttons, quick chips; returns one sync function for the filter sheet and the filter chips.
export function installControls(filters, {toggleSheet, closeSheet, filterBadge, changed, share}) {
  installDock(); chipsCarousel(); chipsPlacement(); installThemeColor();
  // Sheet and chips edit the same filter object: each change refreshes the other side, then the map, badge and URL.
  const syncMenu = filterControls(filters, () => { syncChips(); changed(); });
  const syncChips = chipControls(filters, {apply: () => { syncMenu(); changed(); }});
  const syncFilters = () => { syncMenu(); syncChips(); };
  $('filter-button').onclick = () => toggleSheet('filter');
  installMenu({drawer: installDrawer()});
  $('filter-done').onclick = closeSheet;
  $('details-share').onclick = share;
  filterBadge();
  $('filter').onsubmit = event => event.preventDefault();
  return syncFilters;
}
